import React, { useState } from 'react'
import {
  Search,
  Filter,
  CheckCircle,
  Star,
  Instagram,
  Youtube,
  Users,
  TrendingUp,
  MapPin,
  ChevronDown,
  X,
  Heart
} from 'lucide-react'
import { mockCreators, formatNumber, formatCurrency } from '../../data/mockData'

const specialties = ['All Specialties', 'Neurosurgery', 'Cardiology', 'Pediatrics', 'Mental Health', 'Nursing', 'Patient Advocacy']
const platforms = ['All Platforms', 'Instagram', 'TikTok', 'YouTube', 'LinkedIn']
const tiers = ['All Tiers', 'Flagship', 'Macro', 'Mid-Tier', 'Micro']

const tierColors = {
  flagship: 'bg-purple-100 text-purple-700',
  macro: 'bg-blue-100 text-blue-700',
  mid_tier: 'bg-teal-100 text-teal-700',
  micro: 'bg-gray-100 text-gray-700',
}

const platformIcons = {
  instagram: Instagram,
  tiktok: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
    </svg>
  ),
  youtube: Youtube,
  linkedin: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
}

function BrandCreatorDiscovery() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSpecialty, setSelectedSpecialty] = useState('All Specialties')
  const [selectedPlatform, setSelectedPlatform] = useState('All Platforms')
  const [selectedTier, setSelectedTier] = useState('All Tiers')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedCreator, setSelectedCreator] = useState(null)

  const filteredCreators = mockCreators.filter(creator => {
    const matchesSearch = creator.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         creator.title.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesSpecialty = selectedSpecialty === 'All Specialties' ||
                            creator.specialty.some(s => s.toLowerCase().includes(selectedSpecialty.toLowerCase()))
    const matchesPlatform = selectedPlatform === 'All Platforms' ||
                           Object.keys(creator.platforms).some(p => p.toLowerCase() === selectedPlatform.toLowerCase())
    const matchesTier = selectedTier === 'All Tiers' ||
                       creator.tier.toLowerCase() === selectedTier.toLowerCase().replace('-', '_')
    return matchesSearch && matchesSpecialty && matchesPlatform && matchesTier
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Discover Creators</h1>
        <p className="text-gray-500">Browse our vetted network of healthcare influencers</p>
      </div>

      {/* Search and Filters */}
      <div className="card !p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, specialty, or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-hf-teal-500"
            />
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <select
                value={selectedSpecialty}
                onChange={(e) => setSelectedSpecialty(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-hf-teal-500"
              >
                {specialties.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-hf-teal-500"
              >
                {platforms.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-hf-teal-500"
              >
                {tiers.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-gray-600">
          <span className="font-medium">{filteredCreators.length}</span> creators found
        </p>
      </div>

      {/* Creator Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCreators.map((creator) => (
          <div
            key={creator.id}
            className="card hover:shadow-lg transition-shadow cursor-pointer group"
            onClick={() => setSelectedCreator(creator)}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <img
                    src={creator.avatar}
                    alt={creator.name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  {creator.verified && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-hf-teal-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-hf-teal-600 transition-colors">
                    {creator.name}
                  </h3>
                  <p className="text-sm text-gray-500">{creator.title}</p>
                </div>
              </div>
              <span className={`badge ${tierColors[creator.tier]}`}>
                {creator.tier.replace('_', '-')}
              </span>
            </div>

            {/* Bio */}
            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{creator.bio}</p>

            {/* Specialties */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {creator.specialty.slice(0, 3).map(spec => (
                <span key={spec} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {spec}
                </span>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
              <div>
                <p className="text-lg font-semibold text-gray-900">{formatNumber(creator.totalReach)}</p>
                <p className="text-xs text-gray-500">Reach</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">{creator.avgEngagement}%</p>
                <p className="text-xs text-gray-500">Engagement</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">{creator.campaignsCompleted}</p>
                <p className="text-xs text-gray-500">Campaigns</p>
              </div>
            </div>

            {/* Platforms */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                {Object.entries(creator.platforms).map(([platform, data]) => {
                  const Icon = platformIcons[platform] || Instagram
                  return (
                    <div
                      key={platform}
                      className="flex items-center text-gray-500 text-sm"
                      title={`${platform}: ${formatNumber(data.followers)} followers`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center text-gray-500 text-sm">
                <MapPin className="w-4 h-4 mr-1" />
                {creator.location}
              </div>
            </div>

            {/* Rating */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center">
                <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                <span className="font-medium text-gray-900">{creator.rating}</span>
                <span className="text-gray-400 ml-1">({creator.campaignsCompleted} reviews)</span>
              </div>
              <p className="text-sm font-medium text-gray-600">
                From {formatCurrency(creator.baseRate)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Creator Detail Modal */}
      {selectedCreator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/50"
            onClick={() => setSelectedCreator(null)}
          />
          <div className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setSelectedCreator(null)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              {/* Creator Header */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="relative">
                  <img
                    src={selectedCreator.avatar}
                    alt={selectedCreator.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                  {selectedCreator.verified && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-hf-teal-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-bold text-gray-900">{selectedCreator.name}</h2>
                    <span className={`badge ${tierColors[selectedCreator.tier]}`}>
                      {selectedCreator.tier.replace('_', '-')}
                    </span>
                  </div>
                  <p className="text-gray-600">{selectedCreator.title}</p>
                  <div className="flex items-center text-gray-500 text-sm mt-1">
                    <MapPin className="w-4 h-4 mr-1" />
                    {selectedCreator.location}
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-2">About</h3>
                <p className="text-gray-600">{selectedCreator.bio}</p>
              </div>

              {/* Specialties */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-2">Specialties</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedCreator.specialty.map(spec => (
                    <span key={spec} className="px-3 py-1 bg-hf-teal-50 text-hf-teal-700 text-sm rounded-full">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(selectedCreator.totalReach)}</p>
                  <p className="text-sm text-gray-500">Total Reach</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{selectedCreator.avgEngagement}%</p>
                  <p className="text-sm text-gray-500">Avg. Engagement</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{selectedCreator.onTimeRate}%</p>
                  <p className="text-sm text-gray-500">On-Time Rate</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center">
                    <Star className="w-5 h-5 text-yellow-400 fill-current mr-1" />
                    <span className="text-2xl font-bold text-gray-900">{selectedCreator.rating}</span>
                  </div>
                  <p className="text-sm text-gray-500">Rating</p>
                </div>
              </div>

              {/* Platform Breakdown */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-3">Platform Presence</h3>
                <div className="space-y-3">
                  {Object.entries(selectedCreator.platforms).map(([platform, data]) => {
                    const Icon = platformIcons[platform] || Instagram
                    return (
                      <div key={platform} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center mr-3 shadow-sm">
                            <Icon className="w-4 h-4 text-gray-600" />
                          </div>
                          <span className="font-medium text-gray-900 capitalize">{platform}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">{formatNumber(data.followers)} followers</p>
                          <p className="text-sm text-gray-500">{data.engagement}% engagement</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button className="btn-primary flex-1">
                  <Heart className="w-4 h-4 mr-2" />
                  Add to Campaign
                </button>
                <button className="btn-secondary">
                  View Portfolio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BrandCreatorDiscovery
